/**
 * Il modulo di contatto OMAI, disegnato dentro l'applicazione.
 *
 * I moduli stanno su link.omai.cloud e le richieste finiscono in
 * controll.omai.cloud/richieste, divise in due code: commerciale e assistenza.
 * Qui il form si rifa' con i primitivi del kit invece di incorporare un
 * iframe, perche' la pagina ospitata porta un tema suo e un riquadro ad
 * altezza fissa che dentro questa interfaccia stonerebbero.
 *
 * Rifarlo vuol dire pero' riportare a mano le tre difese della pagina
 * ospitata, senza le quali il server rifiuta l'invio:
 *   - il campo esca `sito_web_azienda`, invisibile a una persona;
 *   - il gettone del cronometro, che viaggia nel campo `_t` (non `gettone`);
 *   - il consenso `_consenso`, preteso dal server e non solo dal browser.
 *
 * I campi non sono scritti qui: arrivano dal GET della rotta, cosi' se in
 * controll cambiano le domande il form cambia con loro.
 *
 * ATTENZIONE: ogni modulo dichiara le origini da cui accetta gli invii. Per
 * entrambi i moduli di questo progetto e' ammesso il solo
 * `https://sistemagelato.omai.cloud`: da dev-sistemagelato.omai.cloud il
 * server risponde 403. Si aggiunge da controll, non da qui.
 */
import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Button, FormGroup, Label, Input, Select, Textarea, Alert, Spinner } from './ui';
import { MODULI_BASE } from '../config/moduli';

type TipoCampo =
  | 'testo'
  | 'email'
  | 'telefono'
  | 'testolungo'
  | 'numero'
  | 'scelta'
  | 'sceltaMultipla'
  | 'consenso';

interface CampoModulo {
  nome: string;
  etichetta: string;
  tipo: TipoCampo;
  obbligatorio: boolean;
  opzioni: string[];
  placeholder?: string;
}

interface Definizione {
  titolo: string;
  sottotitolo: string | null;
  testoPulsante: string | null;
  campi: CampoModulo[];
  campoEsca: string;
  campoTempo: string;
  gettone: string;
}

const CAMPO_CONSENSO = '_consenso';
const TESTO_CONSENSO = "Ho letto l'informativa e acconsento a essere ricontattato";

const Esca = styled.div`
  position: absolute;
  left: -9999px;
  width: 1px;
  height: 1px;
  overflow: hidden;
`;

const RigaConsenso = styled.label`
  display: flex;
  align-items: flex-start;
  gap: var(--space-sm);
  margin: var(--space-md) 0;
  font-size: var(--font-size-sm);
  color: var(--text-secondary, var(--gray-700));
  cursor: pointer;

  input {
    margin-top: 0.2rem;
    flex: 0 0 auto;
  }
`;

const Sottotitolo = styled.p`
  margin: 0 0 var(--space-lg);
  font-size: var(--font-size-sm);
  color: var(--text-secondary, var(--gray-700));
  line-height: 1.6;
`;

const Conferma = styled.div`
  text-align: center;
  padding: var(--space-xl) 0;

  h3 {
    margin: 0 0 var(--space-sm);
    color: var(--text-primary);
  }

  p {
    margin: 0;
    color: var(--text-secondary, var(--gray-700));
  }
`;

interface Props {
  chiave: string;
}

const ModuloContatto: React.FC<Props> = ({ chiave }) => {
  const [definizione, setDefinizione] = useState<Definizione | null>(null);
  const [caricamentoFallito, setCaricamentoFallito] = useState(false);
  const [valori, setValori] = useState<Record<string, string | string[]>>({});
  const [esca, setEsca] = useState('');
  const [consenso, setConsenso] = useState(false);
  const [invioInCorso, setInvioInCorso] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);
  const [messaggioGrazie, setMessaggioGrazie] = useState<string | null>(null);

  // Il gettone porta dentro di se' l'istante in cui e' stato emesso: il server
  // rifiuta un invio partito meno di 3 secondi dopo, e uno partito piu' di 6
  // ore dopo. Per questo lo si ripesca a ogni errore, cosi' un secondo
  // tentativo su una pagina rimasta aperta a lungo non fallisce per lo stesso
  // motivo del primo.
  const caricaDefinizione = useCallback(async () => {
    try {
      const risposta = await fetch(`${MODULI_BASE}/api/moduli/${chiave}`, { cache: 'no-store' });
      if (!risposta.ok) throw new Error('modulo non disponibile');
      setDefinizione((await risposta.json()) as Definizione);
      setCaricamentoFallito(false);
    } catch {
      setCaricamentoFallito(true);
    }
  }, [chiave]);

  useEffect(() => {
    void caricaDefinizione();
  }, [caricaDefinizione]);

  const aggiorna = (nome: string, valore: string | string[]) =>
    setValori((precedenti) => ({ ...precedenti, [nome]: valore }));

  const invia = async (evento: React.FormEvent) => {
    evento.preventDefault();
    if (!definizione) return;

    setInvioInCorso(true);
    setErrore(null);

    const corpo: Record<string, unknown> = {
      ...valori,
      [definizione.campoEsca]: esca,
      [definizione.campoTempo]: definizione.gettone,
      [CAMPO_CONSENSO]: consenso,
      pagina: window.location.href.slice(0, 500),
    };

    try {
      const risposta = await fetch(`${MODULI_BASE}/api/moduli/${chiave}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(corpo),
      });
      const esito = await risposta.json();

      if (!risposta.ok) {
        setErrore(esito?.errore ?? "Qualcosa e' andato storto durante l'invio.");
        void caricaDefinizione();
        return;
      }

      if (esito?.redirect) {
        window.location.href = esito.redirect;
        return;
      }
      setMessaggioGrazie(esito?.messaggio ?? 'Grazie, ti rispondiamo presto.');
    } catch {
      setErrore('Non siamo riusciti a inviare la richiesta. Riprova fra poco.');
      void caricaDefinizione();
    } finally {
      setInvioInCorso(false);
    }
  };

  if (caricamentoFallito) {
    return (
      <Alert $variant="warning">
        Il modulo non si e' caricato.{' '}
        <a href={`${MODULI_BASE}/m/${chiave}`} target="_blank" rel="noopener noreferrer">
          Aprilo in una pagina a parte
        </a>
        .
      </Alert>
    );
  }

  if (!definizione) return <Spinner />;

  if (messaggioGrazie) {
    return (
      <Conferma>
        <h3>Richiesta inviata.</h3>
        <p>{messaggioGrazie}</p>
      </Conferma>
    );
  }

  return (
    <form onSubmit={invia}>
      {definizione.sottotitolo && <Sottotitolo>{definizione.sottotitolo}</Sottotitolo>}

      {definizione.campi.map((campo) => (
        <FormGroup key={campo.nome}>
          <Label htmlFor={`campo-${campo.nome}`}>
            {campo.etichetta}
            {campo.obbligatorio && ' *'}
          </Label>
          <Campo
            campo={campo}
            valore={valori[campo.nome] ?? (campo.tipo === 'sceltaMultipla' ? [] : '')}
            onChange={(valore) => aggiorna(campo.nome, valore)}
            disabilitato={invioInCorso}
          />
        </FormGroup>
      ))}

      {/* L'esca: nascosta agli occhi e al lettore di schermo, appetibile a un
          compilatore automatico. Chi la riempie se ne va con un 200 e la sua
          richiesta non entra. */}
      <Esca aria-hidden="true">
        <input
          type="text"
          name={definizione.campoEsca}
          tabIndex={-1}
          autoComplete="off"
          value={esca}
          onChange={(e) => setEsca(e.target.value)}
        />
      </Esca>

      <RigaConsenso>
        <input
          type="checkbox"
          required
          checked={consenso}
          onChange={(e) => setConsenso(e.target.checked)}
          disabled={invioInCorso}
        />
        <span>{TESTO_CONSENSO}.</span>
      </RigaConsenso>

      {errore && <Alert $variant="error">{errore}</Alert>}

      <Button type="submit" $variant="primary" disabled={invioInCorso}>
        {invioInCorso ? 'Invio in corso...' : definizione.testoPulsante ?? 'Invia la richiesta'}
      </Button>
    </form>
  );
};

interface CampoProps {
  campo: CampoModulo;
  valore: string | string[];
  onChange: (valore: string | string[]) => void;
  disabilitato: boolean;
}

const Campo: React.FC<CampoProps> = ({ campo, valore, onChange, disabilitato }) => {
  const id = `campo-${campo.nome}`;

  if (campo.tipo === 'testolungo') {
    return (
      <Textarea
        id={id}
        rows={5}
        required={campo.obbligatorio}
        disabled={disabilitato}
        placeholder={campo.placeholder}
        value={valore as string}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  if (campo.tipo === 'scelta') {
    return (
      <Select
        id={id}
        required={campo.obbligatorio}
        disabled={disabilitato}
        value={valore as string}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">Scegli...</option>
        {campo.opzioni.map((opzione) => (
          <option key={opzione} value={opzione}>
            {opzione}
          </option>
        ))}
      </Select>
    );
  }

  if (campo.tipo === 'sceltaMultipla') {
    const scelte = Array.isArray(valore) ? valore : [];
    return (
      <div>
        {campo.opzioni.map((opzione) => (
          <RigaConsenso key={opzione}>
            <input
              type="checkbox"
              checked={scelte.includes(opzione)}
              disabled={disabilitato}
              onChange={(e) =>
                onChange(
                  e.target.checked
                    ? [...scelte, opzione]
                    : scelte.filter((scelta) => scelta !== opzione)
                )
              }
            />
            <span>{opzione}</span>
          </RigaConsenso>
        ))}
      </div>
    );
  }

  // `consenso` come tipo di campo e' il consenso *facoltativo* alle
  // comunicazioni commerciali: un'altra cosa dal `_consenso` obbligatorio.
  if (campo.tipo === 'consenso') {
    return (
      <RigaConsenso>
        <input
          type="checkbox"
          id={id}
          required={campo.obbligatorio}
          disabled={disabilitato}
          checked={valore === 'true'}
          onChange={(e) => onChange(e.target.checked ? 'true' : '')}
        />
        <span>{campo.etichetta}</span>
      </RigaConsenso>
    );
  }

  const tipiInput: Record<string, string> = {
    testo: 'text',
    email: 'email',
    telefono: 'tel',
    numero: 'number',
  };

  return (
    <Input
      id={id}
      type={tipiInput[campo.tipo] ?? 'text'}
      required={campo.obbligatorio}
      disabled={disabilitato}
      placeholder={campo.placeholder}
      value={valore as string}
      onChange={(e) => onChange(e.target.value)}
    />
  );
};

export default ModuloContatto;
